import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Form, Input, Modal, Select, notification } from "antd";

import { ComplinEnum, ExistEnum, InspectionOrderItem, InspectItemBody } from "@/types/inspection";
import useInspactionEnum from "../../hooks/useInspactionEnum";
import { itemInspect, itemUpdate, orderProgress } from "@/apis/inspection";
import { InspectionOrderContext } from "./context";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import usePage from "@/layout/hooks/usePage";

interface InspectItemFormProps {
  open: boolean;
  item: InspectionOrderItem;
  onClose: () => void;
  onSucess?: (newItem: InspectionOrderItem) => void;
};

const InspectItemForm: FC<InspectItemFormProps> = ({ open, item, onClose, onSucess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<InspectItemBody>();
  const { ExistEnumList, ComplinEnumList } = useInspactionEnum(false);
  const { setItems, setProgress } = useContext(InspectionOrderContext);
  const [loading, setLoading] = useState<"SAVE" | "SUBMIT" | undefined>(undefined);
  const existenceState = Form.useWatch('existenceState', form);

  useEffect(() => {
    if (existenceState === ExistEnum.NOT) form.setFieldValue('complianceState', undefined);
  }, [existenceState, form]);

  const { curUser } = useSelector((state: RootState) => state.userReducer);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        existenceState: item.existenceState === null ? undefined : item.existenceState,
        complianceState: item.complianceState === null ? undefined : item.complianceState,
        action: item?.action ?? "",
      });
    }
  }, [open, item]);

  const handleOk = async (type: "SAVE" | "SUBMIT") => {
    try {
      setLoading(type);
      let values = await form.validateFields();
      let newItem: InspectionOrderItem = {
        ...item,
      }
      values.docId = item.docId;
      if (values.complianceState === undefined && values.existenceState === undefined) {
        newItem.complianceState = null;
        newItem.existenceState = null;
        newItem.action = values.action;
        await itemUpdate(item.id, values);
      }
      else {
        newItem.complianceState = values.complianceState ?? null;
        newItem.existenceState = values.existenceState ?? null;
        newItem.inspectedAt = Date.now();
        newItem.inspectedBy = curUser.id;
        newItem.inspector = curUser;
        newItem.action = values.action;
        await itemInspect(item.id, values);
        // setProgress(last => {
        //   if (last) return {
        //     ...last,
        //     inspected: last.inspected + 1,
        //     uninspected: last.uninspected - 1,
        //   }
        //   else return null
        // });
      };
      let newProgress = await orderProgress(item.inspectionOrderId);
      setProgress(newProgress);
      setItems(last => last.map(i => {
        if (i.id === newItem.id) return newItem
        else return i
      }));
      notification.success({ message: t(type === "SAVE" ? 'inspection.saveOk' : 'inspection.submitSucc') })
      setLoading(undefined);
      onSucess?.(newItem);
      onClose();
    } catch (error: any) {
      let desc = '';
      if (error === 74305) desc = t('inspection.orderCanceled')
      if (error === 74306) desc = t('inspection.orderFinished')
      notification.error({ message: t(type === "SAVE" ? 'inspection.saveErr' : 'inspection.submitFail'), description: desc })
      setLoading(undefined);
    }
  };

  return (
    <Modal
      title={t('inspection.inspectItem')}
      open={open}
      onCancel={onClose}
      footer={
        <Flex style={{ width: "100%" }} gap={6} justify="flex-end" >
          <Button disabled={!!loading} onClick={onClose}>{t('common.cancel')}</Button>
          <Button loading={loading === "SAVE"} type="primary" onClick={() => handleOk("SAVE")}>{t('common.save')}</Button>
          {/* <Button loading={loading === "SUBMIT"} type="primary" onClick={() => handleOk("SUBMIT")}>{t('inspection.submitInspect')}</Button> */}
        </Flex>
      }
      destroyOnClose
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('inspection.existenceState')}
          name="existenceState"
        // rules={[{ required: true, message: t('inspection.existenceState') }]}
        >
          <Select<ExistEnum>
            options={ExistEnumList}
            placeholder={t('inspection.existenceState')} />
        </Form.Item>
        <Form.Item
          label={t('inspection.complianceState')}
          name="complianceState"
          rules={existenceState === ExistEnum.YES ? [{ required: true, message: t('inspection.complianceState') }] : []}
        >
          <Select<ComplinEnum>
            options={ComplinEnumList}
            disabled={existenceState === ExistEnum.NOT}
            placeholder={t('inspection.complianceState')}
          />
        </Form.Item>
        <Form.Item label={t('inspection.action')} name="action">
          <Input.TextArea rows={3} placeholder={t('inspection.action')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InspectItemForm;
